import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Quote } from "lucide-react";

export const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [tag, setTag] = useState("");
  const [sent, setSent] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/testimonials");
      setItems(res.data);
    } catch { /* wall stays bare */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/testimonials", { text, tag: tag || null });
      setSent(true);
      setText("");
      setTag("");
      toast.success("On the wall. No name attached — that's the point.");
      load();
    } catch {
      toast.error("That didn't land. Try again.");
    }
  };

  return (
    <section id="words" className="py-24 bg-hearth-surface border-y border-hearth-line" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="eyebrow mb-4">Words of Trust</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink leading-tight">Said by people whose names we'll never ask.</h2>
          <p className="mt-6 text-hearth-muted leading-relaxed max-w-md">
            Forty years of quiet work means there are hundreds of these waiting to be written.
            Leave yours — anonymous, always.
          </p>
          {sent ? (
            <p data-testid="testimonial-success" className="mt-10 font-essay italic text-2xl text-hearth-ochre">Thank you. It's on the wall.</p>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="testimonial-form">
              <textarea
                data-testid="testimonial-text-input"
                required
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                maxLength={1200}
                placeholder="What did sitting beside the fire do for you?"
                className="w-full bg-hearth-bg border border-hearth-line px-5 py-4 text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre font-essay text-lg"
              />
              <input
                data-testid="testimonial-tag-input"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                maxLength={80}
                placeholder="A label, if you like — e.g. 'a bloke, 40s' (never your name)"
                className="w-full bg-hearth-bg border border-hearth-line px-5 py-3 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre"
              />
              <button data-testid="testimonial-submit-button" className="bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
                Pin It to the Wall
              </button>
            </form>
          )}
        </div>
        <div className="space-y-6 max-h-[560px] overflow-y-auto pr-2" data-testid="testimonials-wall">
          {items.length === 0 ? (
            <div className="border border-dashed border-hearth-line p-10" data-testid="testimonials-empty">
              <Quote className="w-8 h-8 text-hearth-ember mb-4" strokeWidth={1.5} />
              <p className="font-essay italic text-2xl text-hearth-muted">The wall is bare — for now. The people who could fill it know who they are.</p>
            </div>
          ) : (
            items.map((t) => (
              <figure key={t.id} className="border border-hearth-line bg-hearth-bg p-8 hover:border-hearth-ochre transition-colors" data-testid={`testimonial-card-${t.id}`}>
                <Quote className="w-5 h-5 text-hearth-ember mb-3" strokeWidth={1.5} />
                <blockquote className="font-essay text-xl text-hearth-ink/90 leading-relaxed">{t.text}</blockquote>
                {t.tag && <figcaption className="mt-4 text-xs uppercase tracking-widest text-hearth-muted">— {t.tag}</figcaption>}
              </figure>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
